import {
  Alert,
  Button,
  CellText,
  Checkbox,
  DataTable,
  Dialog,
  FormField,
  Heading,
  Input,
  MoreMenu,
  Section,
  Spinner,
  Stack,
  phoneQuery,
  useMediaQuery,
  useToast,
  type MenuItem,
} from '@ui';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { BackLink } from '../../../app/BackLink';
import { ConfirmDialog } from '../../../app/ConfirmDialog';
import { PageActions } from '../../../app/PageActions';
import { errorMessage, formErrors } from '../../../lib/errors';
import {
  useCreateRole,
  useDeleteRole,
  usePermissionCatalog,
  useRoles,
  useUpdateRole,
  type Permission,
  type PermissionGroup,
  type Role,
} from '../api';
import { grant, permissionRows, revoke, type PermissionRow } from '../permissions';
import { useSession } from '../session';
import { groupLabel, permissionLabel, rowLabel } from './RolesPage';

const LIST = '/einstellungen/rollen';

/**
 * Creates (`/einstellungen/rollen/neu`, optionally `?vorlage=<id>` to copy a
 * role) or shows and edits a role (`/einstellungen/rollen/<id>`).
 */
export function RoleEditorPage() {
  const { t } = useTranslation();
  const { roleId } = useParams();
  const [search] = useSearchParams();
  const { can } = useSession();
  const roles = useRoles();
  const catalog = usePermissionCatalog();
  const error = roles.error ?? catalog.error;

  if (error) return <Alert tone="danger">{errorMessage(error)}</Alert>;
  if (!roles.data || !catalog.data) return <Spinner label={t('common.loading')} />;

  const isNew = roleId === undefined;
  const role = isNew ? undefined : roles.data.find((r) => r.id === roleId);
  if (!isNew && !role) return <Alert tone="danger">{t('errors.role.not_found')}</Alert>;
  const template = roles.data.find((r) => r.id === search.get('vorlage'));
  const canManage = can('roles.manage');
  if (isNew && !canManage) return <Alert tone="danger">{t('errors.forbidden')}</Alert>;

  return (
    <RoleForm
      key={role?.id ?? `new-${template?.id ?? ''}`}
      role={role}
      template={template}
      catalog={catalog.data}
      readOnly={!canManage || Boolean(role?.owner)}
    />
  );
}

function RoleForm({
  role,
  template,
  catalog,
  readOnly,
}: {
  role: Role | undefined;
  template: Role | undefined;
  catalog: PermissionGroup[];
  readOnly: boolean;
}) {
  const { t } = useTranslation();
  const phone = useMediaQuery(phoneQuery);
  const toast = useToast();
  const navigate = useNavigate();
  const create = useCreateRole();
  const update = useUpdateRole();
  const source = role ?? template;
  const [name, setName] = useState(
    role?.name ?? (template ? t('roles.copyName', { name: template.name }) : ''),
  );
  const [granted, setGranted] = useState<Set<Permission>>(
    () => new Set((source?.owner ? [] : source?.permissions) ?? []),
  );
  const [deleting, setDeleting] = useState(false);
  const save = role ? update : create;
  const errors = formErrors(save.error, { name: ['role.name'] });

  const toggle = (permission: Permission, on: boolean) =>
    setGranted((current) =>
      on ? grant(current, permission, catalog) : revoke(current, permission, catalog),
    );

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const body = {
      name,
      permissions: [...granted],
    };
    const done = () => {
      toast.show(t('common.saved'));
      navigate(LIST);
    };
    if (role) update.mutate({ roleId: role.id, body }, { onSuccess: done });
    else create.mutate(body, { onSuccess: done });
  };

  const title = role ? role.name : t('roles.create');
  const menu: MenuItem[] =
    role && !role.owner && !readOnly
      ? [
          {
            key: 'copy',
            label: t('roles.copy'),
            onSelect: () => navigate(`${LIST}/neu?vorlage=${role.id}`),
          },
          {
            key: 'delete',
            label: t('roles.delete'),
            tone: 'danger',
            separated: true,
            onSelect: () => setDeleting(true),
          },
        ]
      : [];

  return (
    <Stack gap={6}>
      {menu.length > 0 && (
        <PageActions>
          <MoreMenu label={t('common.moreActions')} align="end" items={menu} />
        </PageActions>
      )}
      <Stack gap={2}>
        {!phone && <BackLink to={LIST} label={t('roles.title')} />}
        <Heading level={2} size="xl">
          {title}
        </Heading>
      </Stack>
      {role?.owner && <Alert tone="info">{t('roles.ownerFixed')}</Alert>}
      {!role?.owner && readOnly && <Alert tone="info">{t('roles.readOnly')}</Alert>}

      <Stack as="form" gap={6} onSubmit={onSubmit} noValidate>
        {errors.general && <Alert tone="danger">{errors.general}</Alert>}
        {!readOnly && (
          <Section variant="content">
            <FormField label={t('roles.name')} error={errors.field.name} required>
              {(p) => (
                <Input
                  {...p}
                  value={name}
                  maxLength={50}
                  onChange={(e) => setName(e.target.value)}
                />
              )}
            </FormField>
          </Section>
        )}

        {catalog.map((group) => (
          <Section key={group.key} title={groupLabel(t, group.key)} variant="flush">
            <PermissionTable
              group={group}
              granted={role?.owner ? new Set(role.permissions) : granted}
              readOnly={readOnly}
              onToggle={toggle}
            />
          </Section>
        ))}

        {!readOnly && (
          <Stack direction="row" gap={2} wrap>
            <Button type="submit" loading={save.isPending}>
              {t('common.save')}
            </Button>
            <Button variant="secondary" onClick={() => navigate(LIST)}>
              {t('common.cancel')}
            </Button>
          </Stack>
        )}
      </Stack>
      {role && <DeleteDialog role={role} open={deleting} onClose={() => setDeleting(false)} />}
    </Stack>
  );
}

function PermissionTable({
  group,
  granted,
  readOnly,
  onToggle,
}: {
  group: PermissionGroup;
  granted: ReadonlySet<Permission>;
  readOnly: boolean;
  onToggle: (permission: Permission, on: boolean) => void;
}) {
  const { t } = useTranslation();

  const cell = (permission: PermissionGroup['permissions'][number] | undefined) =>
    // Without a permission the table shows its grey dash.
    permission ? (
      <Checkbox
        label={permissionLabel(t, permission.key)}
        labelHidden
        description={permission.owner_only && !readOnly ? t('roles.ownerOnly') : undefined}
        checked={granted.has(permission.key)}
        disabled={readOnly || permission.owner_only}
        onChange={(e) => onToggle(permission.key, e.target.checked)}
      />
    ) : null;

  // The same table as everywhere (#177, #197): one inset hairline per row,
  // none after the last; it stays a table on phones (two narrow columns).
  return (
    <DataTable
      caption={groupLabel(t, group.key)}
      layout="table"
      rows={permissionRows(group)}
      rowKey={(row) => row.key}
      columns={[
        {
          key: 'area',
          header: t('roles.area'),
          cell: (row: PermissionRow) => (
            <CellText
              main={rowLabel(t, row)}
              sub={row.write?.owner_only && !readOnly ? t('roles.ownerOnly') : undefined}
            />
          ),
        },
        {
          key: 'read',
          header: t('roles.read'),
          align: 'center',
          width: '6.5rem',
          cell: (row: PermissionRow) => cell(row.read),
        },
        {
          key: 'write',
          header: t('roles.write'),
          align: 'center',
          width: '6.5rem',
          cell: (row: PermissionRow) => cell(row.write),
        },
      ]}
    />
  );
}

/**
 * Deleting is final, so it asks first. A role still assigned to members
 * cannot be deleted; the dialog then says why and what to do (§6.10).
 */
function DeleteDialog({ role, open, onClose }: { role: Role; open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const remove = useDeleteRole();

  if (role.member_count > 0) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        title={t('roles.deleteTitle', { name: role.name })}
        description={t('roles.inUse', { count: role.member_count })}
        closeLabel={t('common.close')}
        size="sm"
        actions={<Button onClick={onClose}>{t('common.close')}</Button>}
      />
    );
  }
  return (
    <ConfirmDialog
      open={open}
      onClose={() => {
        remove.reset();
        onClose();
      }}
      title={t('roles.deleteTitle', { name: role.name })}
      description={t('roles.deleteText')}
      confirmLabel={t('roles.delete')}
      pending={remove.isPending}
      error={remove.error ? errorMessage(remove.error) : undefined}
      onConfirm={() =>
        remove.mutate(role.id, {
          onSuccess: () => {
            toast.show(t('roles.deleted', { name: role.name }));
            navigate(LIST);
          },
        })
      }
    />
  );
}
